import React from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';

// Fallback para dados antigos em cache, que ainda não trazem statusDescription.
export const STATUS_LABELS = {
  SCHEDULED: 'Agendada',
  CONFIRMED: 'Confirmada',
  CONFIRMED_BY_PATIENT: 'Confirmada pelo paciente',
  PERFORMED: 'Realizada',
  NO_SHOW: 'Faltou',
  CANCELED: 'Cancelada',
  REARRANGED: 'Reagendada',
};

export const getStatusText = ({ status, statusDescription }) =>
  statusDescription ?? STATUS_LABELS[status] ?? status;

const formatPeriod = (start, end) => {
  if (!start) return '';
  const startDate = dayjs(start);
  const endDate = end ? dayjs(end) : null;
  const period = endDate ? ` das ${startDate.format('HH:mm')} às ${endDate.format('HH:mm')}` : '';
  return `${startDate.format('dddd, DD/MM/YYYY')}${period}`;
};

// Conteúdo do balão exibido ao passar o mouse sobre um evento do calendário.
// Usa só o que já está no evento — nenhuma chamada extra à API.
function EventTooltipContent({
  title,
  start,
  end,
  status,
  statusDescription,
  color,
  observation,
  appointmentTypeDescription,
}) {
  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Typography variant="caption" component="div" sx={{ opacity: 0.9 }}>
        {formatPeriod(start, end)}
      </Typography>
      {appointmentTypeDescription && (
        <Typography variant="caption" component="div" sx={{ opacity: 0.9 }}>
          {appointmentTypeDescription}
        </Typography>
      )}
      {status && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: color || 'currentColor',
              flexShrink: 0,
            }}
          />
          <Typography variant="caption">{getStatusText({ status, statusDescription })}</Typography>
        </Box>
      )}
      {observation && (
        <Typography
          variant="caption"
          component="div"
          sx={{
            mt: 0.5,
            pt: 0.5,
            borderTop: '1px solid',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            whiteSpace: 'pre-wrap',
          }}
        >
          {observation}
        </Typography>
      )}
    </Box>
  );
}

EventTooltipContent.propTypes = {
  title: PropTypes.string,
  start: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  end: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  status: PropTypes.string,
  statusDescription: PropTypes.string,
  color: PropTypes.string,
  observation: PropTypes.string,
  appointmentTypeDescription: PropTypes.string,
};

EventTooltipContent.defaultProps = {
  title: '',
  start: null,
  end: null,
  status: null,
  statusDescription: null,
  color: null,
  observation: null,
  appointmentTypeDescription: null,
};

// Envolve o conteúdo do evento no calendário. `followCursor` evita que o balão
// fique preso fora da célula em eventos curtos, e o delay impede que o balão
// pisque ao arrastar o mouse pela grade.
function EventTooltip({ event, children }) {
  return (
    <Tooltip
      arrow
      followCursor
      enterDelay={400}
      enterNextDelay={200}
      placement="top"
      slotProps={{ tooltip: { sx: { maxWidth: 280 } } }}
      title={
        <EventTooltipContent
          title={event.title}
          start={event.start}
          end={event.end}
          status={event.extendedProps?.status}
          statusDescription={event.extendedProps?.statusDescription}
          color={event.backgroundColor}
          observation={event.extendedProps?.observation}
          appointmentTypeDescription={event.extendedProps?.appointmentTypeDescription}
        />
      }
    >
      {children}
    </Tooltip>
  );
}

EventTooltip.propTypes = {
  event: PropTypes.shape({
    title: PropTypes.string,
    start: PropTypes.instanceOf(Date),
    end: PropTypes.instanceOf(Date),
    backgroundColor: PropTypes.string,
    extendedProps: PropTypes.object,
  }).isRequired,
  children: PropTypes.element.isRequired,
};

export default EventTooltip;
